import { Dispatch, Store } from 'redux';

import * as ENV from '@config/env';

import { Region, RegionToCIAMAppId } from '@eon-home/react-library';

import { Configuration } from '@swagger-http';

import { store } from '@src/index';
import moment from 'moment';
import { isLocalhost } from './helpers';
import { disconnectLiveData, getNewToken, requestLogout } from '@store/actions';
import {
    API_URL_DEFAULT,
    CIAM_LOGOUT,
    LOCALE,
    LOGIN_URL,
    REFRESH_TOKEN,
    REGION,
    SCOPES,
    TOKEN_THRESHOLD,
    TOKEN,
} from '../constants';
import {
    CognitoAccessToken,
    tryDecodeToken,
    tryGetLogoutLink,
    tryGetTokenExpiry,
} from '@tools/utils/oauth';
import { RootState } from '@store/types';

let isLoggingOut = false;

export const getAccessToken = (): string =>
    ENV.accessToken ||
    store.getState().auth.token ||
    localStorage.getItem(TOKEN) ||
    '';

export const generateAccessTokenThreshold = (accessToken: string): number =>
    tryGetTokenExpiry(accessToken) ?? moment().add(40, 'minutes').unix();

export const isTokenExpired = (storeInstance: Store<RootState>) => {
    const { token, threshold } = storeInstance.getState().auth;
    return !!token && moment().unix() > threshold;
};

export const getOrRefreshAccessToken: () => Promise<string> = async () => {
    if (isTokenExpired(store)) {
        await getNewToken(store);
    }
    return store.getState().auth.token;
};

export const setTokenInLocalStorage = (token: string, refreshToken: string) => {
    localStorage.setItem(TOKEN, token);
    localStorage.setItem(REFRESH_TOKEN, refreshToken);
    localStorage.setItem(
        TOKEN_THRESHOLD,
        generateAccessTokenThreshold(token).toString(),
    );
};

export const createRequestConfiguration = (
    basePath = API_URL_DEFAULT,
): Configuration => {
    const accessToken = getAccessToken();

    return new Configuration({
        basePath,
        ...(accessToken
            ? {
                  accessToken: `Bearer ${accessToken}`,
              }
            : {}),
    });
};

export const cleanUpLocalStorage = () => {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(SCOPES);
    localStorage.removeItem(LOCALE);
    localStorage.removeItem(REGION);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_THRESHOLD);
};

export const goToLogin = (params: string = ''): void => {
    const url: string = isLocalhost ? `http://localhost:4200/login` : LOGIN_URL;
    const finalUrl = url + (params ? '?error=' + params : '');
    if (window.location.href !== finalUrl) {
        window.location.href = finalUrl;
    }
};

export const logOut = async (dispatch: Dispatch<any>): Promise<void> => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    disconnectLiveData();

    const appid = (RegionToCIAMAppId as any)[
        localStorage.getItem(REGION) as Region
    ];

    // Handle Logout flow using HEMS SP
    const token = localStorage.getItem(TOKEN);
    const decodedJwtToken = token ? tryDecodeToken(token) : null;

    let redirected = false;
    if (decodedJwtToken) {
        redirected = await goToHemsSPLogout(decodedJwtToken, appid);
    }

    if (!redirected) {
        await dispatch(requestLogout(goToCIAMLogout(appid)));
    }

    isLoggingOut = false;
};

export const goToCIAMLogout = (appid: string): (() => void) => {
    return () => {
        window.location.href = CIAM_LOGOUT + '?appid=' + appid;
    };
};

export const goToHemsSPLogout = async (
    token: CognitoAccessToken,
    ciamAppId: string | undefined = undefined,
): Promise<boolean> => {
    const logoutUrl = await tryGetLogoutLink(token, ciamAppId);

    if (logoutUrl) {
        cleanUpLocalStorage();
        window.location.href = logoutUrl;
        return true;
    }

    return false;
};
